import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface HeroContent {
  title: string;
  subtitle: string;
  description: string;
  cta_primary_text: string;
  cta_primary_link: string;
  cta_secondary_text: string;
  cta_secondary_link: string;
  background_image: string | null;
}

export default function HeroSection() {
  const [content, setContent] = useState<HeroContent | null>(null);

  useEffect(() => {
    async function fetchHeroContent() {
      try {
        const { data, error } = await supabase
          .from('home_hero')
          .select('*')
          .eq('is_active', true)
          .single();

        if (error) throw error;
        if (data) setContent(data);
      } catch (error) {
        console.error('Error fetching hero content:', error);
      }
    }

    fetchHeroContent();
  }, []);

  if (!content) return null;

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 text-white overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: content.background_image ? `url('${content.background_image}')` : undefined
          }}
        ></div>
        {/* Gray overlay for readability */}
        <div className="absolute inset-0 bg-slate-900/70"></div>
      </div>
      
      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32 text-center">
        <div className="inline-flex items-center space-x-2 bg-yellow-600/20 text-yellow-500 px-4 py-2 rounded-full text-sm font-medium mb-8">
          <i className="ri-vip-crown-line"></i>
          <span>Offre premium</span>
        </div>
        
        <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
          {content.title}
        </h1>
        
        <p className="text-xl md:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed">
          {content.description}
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href={content.cta_primary_link} className="inline-flex items-center justify-center whitespace-nowrap cursor-pointer font-medium transition-all duration-300 bg-gradient-to-r from-yellow-600 to-yellow-700 text-white hover:from-yellow-700 hover:to-yellow-800 shadow-lg hover:shadow-xl px-8 py-3.5 text-base">
            {content.cta_primary_text}
            <i className="ri-arrow-right-line ml-2"></i>
          </a>
          <a href={content.cta_secondary_link} className="inline-flex items-center justify-center whitespace-nowrap cursor-pointer font-medium transition-all duration-300 border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-3.5 text-base">
            {content.cta_secondary_text}
          </a>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24 max-w-4xl mx-auto">
          <div>
            <div className="text-4xl font-bold text-yellow-500 mb-2">3</div>
            <div className="text-slate-400 text-sm">Semaines</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-yellow-500 mb-2">15</div>
            <div className="text-slate-400 text-sm">Pages rédigées</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-yellow-500 mb-2">100%</div>
            <div className="text-slate-400 text-sm">Sur mesure</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-yellow-500 mb-2">SEO</div>
            <div className="text-slate-400 text-sm">Optimisé</div>
          </div>
        </div>
      </div>
    </section>
  );
}
